import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { PermissionService } from '../services/permission.service';

/**
 * Guards the routes that RAISE a leave request.
 *
 * Two parties may raise one, and no others: the academic officer the leave
 * belongs to, and the Department / Center user who administers it. Reviewers
 * further up the chain act on requests, they do not create them.
 *
 * The dashboard only offers the action to Department / Center users, but the
 * route carried no group requirement at all, so anyone signed in could reach
 * the blank form by URL and — because the form's own "submit for others" check
 * is wider than this rule — be offered the on-behalf path.
 *
 * Deliberately NOT applied to `leave-request/:id`: opening an existing request
 * is how every reviewer does their job.
 *
 * Named testers are admitted because the bypass lists exist precisely so one
 * account can walk the whole flow end to end. Those lists are empty in
 * production (see features/leave-request-testing-bypasses.feature), and the
 * backend authorises independently either way.
 */
export const raiseLeaveRequestGuard: CanActivateFn = () => {
  const permissionService = inject(PermissionService);
  const router = inject(Router);

  const mayRaise =
    permissionService.hasAnyGroup(['department_center_level', 'academic-officer']) ||
    permissionService.canBypassReviewRoles() ||
    permissionService.canBypassAttachmentGates() ||
    permissionService.canSelfSubmitWhileImpersonating();

  if (mayRaise) return true;

  router.navigate(['/unauthorized']);
  return false;
};
